import {
  Formik,
  useFormikContext,
  validateYupSchema,
  yupToFormErrors,
} from "formik";
import { Suspense, lazy, useEffect, useState } from "react";
import { boolean, object } from "yup";

import {
  Alert,
  Card,
  Empty,
  ProgressStepper,
  Span,
} from "@icg360/design-system";

import { MSSDrawer } from "components/mss-drawer";
import { MainLayout } from "components/shared/main-layout";
import { type Coverage } from "data/keystone-api-mapping";
import {
  useEligibleCoverages,
  useKeystone,
  usePolicyDetail,
  usePolicyPlatformId,
} from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { formatDate, logException, scrollToTop, trackEvent } from "utils";

import { useCoverages } from "../hooks";
import {
  type CoverageError,
  actionTypes,
  stepsReducer,
  useSteps,
} from "./hooks";
import styles from "./update-coverage.module.scss";

const CoverageCard = lazy(() => import("./coverage-card/index"));

export type CoverageUpdateFormSchema = {
  IDENTITY_FRAUD?: boolean;
  MECHANICAL_BREAKDOWN?: boolean;
  SERVICE_LINE?: boolean;
};

type CoverageUpdateFormProps = {
  coveragePage: Record<string, unknown>;
  setCoveragePage: (string) => void;
  step: string;
  error: CoverageError;
  dispatchError: (CoverageError) => void;
  coverages: Coverage[];
  setCoverageStep: () => void;
  setReviewStep: () => void;
};

const CoverageUpdateForm = ({
  coveragePage,
  setCoveragePage,
  step,
  error,
  dispatchError,
  coverages,
  setCoverageStep,
  setReviewStep,
}: CoverageUpdateFormProps) => {
  const { values, isSubmitting, setFieldValue, submitForm } =
    useFormikContext<CoverageUpdateFormSchema>();
  const [cart, setCart] = useState<Record<string, number>>({});
  const [date, setDate] = useState("");
  const [total, setTotal] = useState(0);
  const [leftLoading, setLeftLoading] = useState<number>(
    coverages?.length ?? 0
  );

  // for some reason, this value is 0 at render and changes to the actual length later.
  useEffect(() => {
    setLeftLoading(coverages?.length ?? 0);
  }, [coverages]);

  useEffect(() => {
    const evalTotal = Object.values(cart).reduce((a, b) => a + b, 0);
    setTotal(evalTotal);

    if (error.set && evalTotal > 0) {
      dispatchError({ type: "unset" });
    }
  }, [cart, dispatchError, error.set]);

  const close = () => {
    setCoveragePage({ y: 0, policyId: "", type: "close" });
  };

  const next = () => {
    if (total > 0) {
      dispatchError({ type: "unset" });
      setReviewStep();
    } else {
      dispatchError({ type: "minimum" });
    }
  };

  const FormMessageLayout = ({ msg }) => {
    const iconToUse = msg.type === "success" ? "Done" : "Error";
    return (
      <Empty duotoneIcon={iconToUse} heading={msg.title}>
        <Span color="default">{msg.description}</Span>
      </Empty>
    );
  };

  const FormButtonsLayout = () => {
    if (coveragePage.type === "success") {
      return <MSSDrawer.Footer primaryLabel="Close" onPrimary={close} />;
    }

    if (step === actionTypes.coverages && leftLoading < 0) {
      return (
        <MSSDrawer.Footer
          primaryLabel="Continue"
          onPrimary={next}
          primaryDisabled={error.set && total <= 0}
          primaryButtonTestId="reviewButton"
          secondaryLabel="Cancel"
          onSecondary={close}
          secondaryButtonTestId="coverageGoBackButton"
        />
      );
    }

    if (step === actionTypes.review && leftLoading <= 0) {
      return (
        <MSSDrawer.Footer
          primaryLabel="Add coverage"
          onPrimary={() => {
            setFieldValue("total", total);
            submitForm();
          }}
          primaryDisabled={isSubmitting}
          primaryLoading={isSubmitting}
          primaryButtonTestId="submitButton"
          secondaryLabel="Cancel"
          onSecondary={() => setCoverageStep()}
          secondaryButtonTestId="reviewGoBackButton"
        />
      );
    }

    if (error.set && error.type !== "minimum" && leftLoading <= 0) {
      return (
        <MSSDrawer.Footer
          primaryLabel="Continue"
          onPrimary={next}
          primaryDisabled={true}
          secondaryLabel="Cancel"
          onSecondary={() =>
            setCoveragePage({ y: 0, policyId: "", type: "close" })
          }
          secondaryButtonTestId="errorCancelButton"
        />
      );
    }

    return (
      <MSSDrawer.Footer
        primaryLabel="Continue"
        onPrimary={close}
        primaryDisabled={true}
        secondaryLabel="Cancel"
        onSecondary={close}
      />
    );
  };

  let Main = (
    <>
      <CoveragesLoading />
      {/* we have to call each coverage so their prorated values can be calculated, individual cards will remain hidden until all have been loaded */}
      {coverages?.map((coverage, index) => (
        <CoverageCard
          dispatchError={dispatchError}
          step={step}
          coverage={coverage}
          setCart={setCart}
          setDate={setDate}
          leftLoading={leftLoading}
          setLeftLoading={setLeftLoading}
          key={index}
        />
      ))}
    </>
  );

  if (leftLoading <= 0) {
    Main = (
      <div>
        {error.set && (
          <Alert
            className={styles.alertStyle}
            appearance="danger"
            title={error.title}
            description={error.description}
          />
        )}
        <div>
          <p className={styles.description}>
            The price next to each coverage name is pro-rated for your current
            policy term.
          </p>
          <div className={styles.cardContainer}>
            {coverages?.map((coverage, index) => (
              <CoverageCard
                dispatchError={dispatchError}
                step={step}
                coverage={coverage}
                setCart={setCart}
                setDate={setDate}
                leftLoading={leftLoading}
                setLeftLoading={setLeftLoading}
                key={index}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (step === actionTypes.review && leftLoading <= 0) {
    Main = (
      <Card
        title="Let's review before you purchase"
        className={styles.sectionCard}
        border={false}
      >
        {error.set && (
          <Alert
            className={styles.alertStyle}
            appearance="danger"
            title={error.title}
            description={error.description}
          />
        )}
        <div>
          <p className={styles.description}>
            {`Your coverage will start on ${formatDate(
              date
            )} and will be added to your next billing statement.`}
          </p>

          <div className={styles.cardContainer}>
            {coverages?.map(
              (coverage, index) =>
                coverage?.name &&
                values[coverage.name] && (
                  <CoverageCard
                    dispatchError={dispatchError}
                    step={step}
                    coverage={coverage}
                    setCart={setCart}
                    setDate={setDate}
                    leftLoading={leftLoading}
                    setLeftLoading={setLeftLoading}
                    key={index}
                  />
                )
            )}
          </div>
        </div>
      </Card>
    );
  }

  if (error.set && error.type !== "minimum" && leftLoading <= 0) {
    Main = <FormMessageLayout msg={error} />;
  }

  if (coveragePage.type && leftLoading <= 0) {
    Main = <FormMessageLayout msg={coveragePage} />;
  }

  return (
    <Suspense
      fallback={
        <MainLayout className={styles.maxWidth}>
          <CoveragesLoading />
        </MainLayout>
      }
    >
      <CoverageHeader
        coveragePage={coveragePage}
        error={error}
        leftLoading={leftLoading}
        step={step}
      />
      <MSSDrawer.Body className={styles.drawerContent}>
        <MainLayout className={styles.maxWidth}>{Main}</MainLayout>
      </MSSDrawer.Body>
      <FormButtonsLayout />
    </Suspense>
  );
};

const CoverageHeader = ({ coveragePage, error, leftLoading, step }) => {
  if ((error.set && error.type !== "minimum") || coveragePage.type) {
    return null;
  }

  if (leftLoading <= 0) {
    return (
      <div className={styles.progressWrapper}>
        <ProgressStepper
          appearance="default"
          steps={[
            {
              label: "Add Coverage",
              status: `${
                step === actionTypes.coverages || step === actionTypes.review
                  ? "in-progress"
                  : "incomplete"
              }`,
            },
            {
              label: "Review",
              status: `${
                step === actionTypes.review ? "in-progress" : "incomplete"
              }`,
            },
          ]}
        />
      </div>
    );
  }

  return null;
};

const CoveragesLoading = () => (
  <Empty
    duotoneIcon="MoneyMoving"
    heading="We're checking your coverage prices."
  >
    <Span color="default">
      This will take a few moments. Please do not close your browser or refresh
      the screen.
    </Span>
  </Empty>
);

type CoverageUpdateProps = {
  error: CoverageError;
  dispatchError: (CoverageError) => void;
  setCoveragePage: (string) => void;
  coveragePage: Record<string, unknown>;
};

const CoverageUpdate = ({
  error,
  dispatchError,
  setCoveragePage,
  coveragePage,
}: CoverageUpdateProps) => {
  const keystone = useKeystone();
  const policyNum = usePolicyPlatformId();
  const { mutate: refetchPolicy } = usePolicyDetail();
  const { data, isLoading } = useEligibleCoverages();

  useEffect(() => {
    scrollToTop();
    dispatchError({ type: "unset" });
  }, [dispatchError]);

  useEffect(() => {
    if (error.set) {
      scrollToTop();
    }
  }, [error]);

  const coveragesToShow = useCoverages(data?.coverages);
  const { step, setCoverageStep, setReviewStep } = useSteps({
    reducer: stepsReducer,
  });

  if (isLoading || !coveragesToShow) {
    return null;
  }

  // map each coverage value to false and return that array
  // if we don't initialize this state, react will throw a warning when we use them in a checkbox
  const initialValues = Object.values(data?.coverages ?? []).reduce(
    (o, coverage) => ({ ...o, [coverage?.name ?? ""]: false }),
    {}
  );

  const validate = async (values) => {
    const coveragesSchemaResolver = Object.values(data?.coverages ?? []).reduce(
      (o, coverage) => ({ ...o, [coverage?.name ?? ""]: boolean() }),
      {}
    );
    const coveragesSchema = object().shape(coveragesSchemaResolver);

    try {
      await validateYupSchema(values, coveragesSchema, true);
    } catch (error) {
      return yupToFormErrors(error);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const { ...coverages } = values;

    async function purchase(eligibleCoverages, coverages) {
      setSubmitting(true);
      const successful: string[] = [];
      const failed: string[] = [];
      for (const coverage of eligibleCoverages) {
        if (coverages[coverage.name]) {
          const res = await keystone.purchaseCoverage({
            policyNum: policyNum ?? "",
            coverageName: coverage.name,
            protectionValue: coverage.protectionValue,
          });

          if (isError(res)) {
            trackEvent("Additional coverages error -- Purchasing failed", {
              coverage: coverage.name,
              policyId: policyNum,
            });
            failed.push(coverage.name);
            return { successful, failed };
          }
          trackEvent("Additional coverages -- Purchasing succeeded", {
            coverage: coverage.name,
            policyId: policyNum,
          });
          successful.push(coverage.name);
        }
      }

      return { successful, failed };
    }

    try {
      setSubmitting(true);
      const { coverages: eligibleCoverages } = data ?? {};

      const { successful, failed } = await purchase(
        eligibleCoverages,
        coverages
      );
      setSubmitting(false);

      if (failed.length > 0) {
        const numCovered = failed.length + successful.length;

        if (numCovered === failed.length) {
          setCoveragePage({ policyId: policyNum, type: "danger" });
        } else {
          setCoveragePage({
            policyId: policyNum,
            type: "warning",
            failed,
          });
        }

        throw new Error(
          `succeeded: ${successful.join(" ")}, failed: ${failed.join(" ")}`
        );
      } else {
        setCoveragePage({ policyId: policyNum, type: "success" });
      }
      refetchPolicy();
    } catch (err) {
      logException(err);
      dispatchError({});
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      enableReinitialize
    >
      <CoverageUpdateForm
        coveragePage={coveragePage}
        setCoveragePage={setCoveragePage}
        step={step}
        setCoverageStep={setCoverageStep}
        setReviewStep={setReviewStep}
        coverages={coveragesToShow}
        error={error}
        dispatchError={dispatchError}
      />
    </Formik>
  );
};

export default CoverageUpdate;
